import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { BsArrowLeft, BsStarFill } from "react-icons/bs";
import { Skeleton } from "../../Components/Needed";
import ticketApi from "../../Helpers/ticketApi";
import Dialog from "./Dialog";
import { Activity, Composer, Thread } from "./Thread";
import { EVENT_LABEL, PRIORITIES, PRIORITY_CLASS, PRIORITY_LABEL, STATUS_CLASS, assigneeText, clockText, dayText, when } from "./shared";
import "../../Assets/css/tickets.css";

// One ticket for an admin (Phase 7.8): the thread with notes, the SLA clocks, the activity, and every action.
const TicketDetail = () => {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [missing, setMissing] = useState(false);
    const [dialog, setDialog] = useState(null); // resolve | assign | appeal | return | close | reopen
    const [text, setText] = useState("");
    const [people, setPeople] = useState([]);
    const [person, setPerson] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async (quiet) => {
        const result = await ticketApi.detail(ticketId);
        if (!result.ok) { if (!quiet) { setMissing(true); toast.error(result.message); } return; }
        setData(result.data);
    }, [ticketId]);
    useEffect(() => { setData(null); setMissing(false); load(); }, [load]);
    // New messages show within 30 seconds while the page is open.
    useEffect(() => { const t = setInterval(() => load(true), 30000); return () => clearInterval(t); }, [load]);

    const open = async (kind) => {
        setText(""); setPerson(""); setDialog(kind);
        if (kind === "assign" && !people.length) {
            const r = await ticketApi.handlers();
            if (r.ok) setPeople(r.data.people || []); else toast.error(r.message);
        }
    };
    const run = async (work, done = "Done") => {
        setBusy(true);
        const result = await work();
        setBusy(false);
        if (!result.ok) { toast.error(result.message); if (result.code === "stale") load(true); return result; }
        toast.success(result.message || done);
        setDialog(null);
        await load(true);
        return result;
    };

    if (missing) return <div className="settings-page"><Link to="/tickets"><BsArrowLeft aria-hidden="true" /> Tickets</Link><p className="mt-3 text-muted">This ticket was not found.</p></div>;
    if (!data) return <div className="settings-page"><Skeleton height={420} /></div>;

    const { ticket: t, can = {}, messages = [], events = [] } = data;
    const s = t.slaState || {};
    const clocks = [clockText(s.response, "First reply", s.paused), clockText(s.resolution, "Resolution", s.paused)].filter(Boolean);
    const isStaffTicket = t.kind === "staff";
    const raiserLabel = isStaffTicket ? "the staff member" : "the customer";

    return (
        <div className="settings-page tk-page">
            <button type="button" className="btn btn-link px-0 mb-2" onClick={() => navigate(-1)}><BsArrowLeft aria-hidden="true" /> Back</button>
            <div className="tk-head">
                <div>
                    <div className="small text-muted">{t.ticketId} · {isStaffTicket ? "Staff ticket" : "Customer ticket"} · {t.category}{t.reason ? ` / ${t.reason}` : ""}</div>
                    <h4 className="fw-semibold mb-1">{t.subject}</h4>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                        <span className={`tk-status ${STATUS_CLASS[t.status] || ""}`}>{t.status}</span>
                        <span className={`tk-priority ${PRIORITY_CLASS[t.priority] || ""}`}>{PRIORITY_LABEL[t.priority]}</span>
                        {t.appeal?.active ? <span className="tk-flag">{t.appeal.by === "system" ? "Escalated" : "Appealed"}</span> : null}
                        {t.reopenCount ? <span className="tk-flag tk-flag-muted">Reopened {t.reopenCount}×</span> : null}
                    </div>
                </div>
                <div className="tk-actions">
                    {can.take ? <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => run(() => ticketApi.take(t.ticketId), "Taken")}>Take</button> : null}
                    {can.decideAppeal ? <button type="button" className="btn btn-warning btn-sm" onClick={() => open("appeal")}>Decide appeal</button> : null}
                    {can.reassign ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => open("assign")}>{t.assignee?.id ? "Reassign" : "Assign"}</button> : null}
                    {can.returnToPool ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => open("return")}>Return to pool</button> : null}
                    {can.setStatus && t.status === "In Progress" ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => run(() => ticketApi.status(t.ticketId, "Waiting on customer"))}>Waiting on {isStaffTicket ? "staff" : "customer"}</button> : null}
                    {can.setStatus && t.status === "Waiting on customer" ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => run(() => ticketApi.status(t.ticketId, "In Progress"))}>Back to In Progress</button> : null}
                    {can.resolve ? <button type="button" className="btn btn-success btn-sm" onClick={() => open("resolve")}>Resolve</button> : null}
                    {can.reopen ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => open("reopen")}>Reopen</button> : null}
                    {can.close ? <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => open("close")}>Close</button> : null}
                </div>
            </div>

            <div className="tk-grid">
                <section className="tk-main" aria-label="Conversation">
                    <Thread ticket={t} messages={messages} />
                    {t.status === "Resolved" ? <div className="alert alert-light border small mt-3">Resolved {when(t.resolvedAt)}. It closes on its own unless {raiserLabel} says it isn't solved.</div> : null}
                    {t.status === "Closed" ? <div className="alert alert-light border small mt-3">Closed {when(t.closedAt)}{t.closedBy === "raiser" ? ` by ${raiserLabel} (solved)` : t.closedBy === "auto" ? " automatically" : ""}.</div> : null}
                    <Composer
                        canReply={!!can.reply}
                        canNote={!!can.note}
                        canWait={!!can.setStatus}
                        raiserLabel={raiserLabel}
                        noteHint={isStaffTicket ? "Only admins see notes on a staff ticket." : "Only staff and admins see notes."}
                        onSend={async (fields, files) => {
                            const r = await ticketApi.message(t.ticketId, fields, files);
                            if (!r.ok) toast.error(r.message); else await load(true);
                            return r;
                        }}
                    />
                    {!can.reply && !can.note && ["Open", "In Progress", "Waiting on customer"].includes(t.status) && can.take ? <p className="small text-muted mt-2">Take the ticket to reply.</p> : null}
                </section>

                <aside className="tk-side">
                    <div className="tk-card">
                        <h6>Details</h6>
                        <dl className="tk-dl">
                            <dt>Raised by</dt>
                            <dd>{isStaffTicket ? `${t.raisedBy?.name || ""} (${t.raisedBy?.id})` : <Link to={`/customers/${t.raisedBy?.id}`}>{t.raisedBy?.name || t.raisedBy?.id}</Link>}</dd>
                            {t.bookingId ? <><dt>Booking</dt><dd><Link to={`/booking/details/${t.bookingId}`}>{t.bookingId}</Link></dd></> : null}
                            {t.hubId ? <><dt>Hub</dt><dd>{t.hubId}</dd></> : null}
                            <dt>With</dt><dd>{assigneeText(t)}</dd>
                            <dt>Raised</dt><dd>{when(t.createdAt)}</dd>
                            <dt>Priority</dt>
                            <dd>
                                {can.priority ? (
                                    <select className="form-select form-select-sm" aria-label="Priority" value={t.priority} disabled={busy} onChange={(e) => run(() => ticketApi.priority(t.ticketId, e.target.value), "Priority changed")}>
                                        {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
                                    </select>
                                ) : PRIORITY_LABEL[t.priority]}
                            </dd>
                        </dl>
                        {t.appeal?.active || t.appeal?.at ? (
                            <div className="tk-appeal">
                                <div className="fw-semibold small">{t.appeal.active ? "Waiting for an admin" : `Appeal ${t.appeal.outcome || ""}`}</div>
                                <div className="small">{t.appeal.byName || t.appeal.by}: {t.appeal.reason}</div>
                                {t.appeal.note ? <div className="small text-muted">Admin: {t.appeal.note}</div> : null}
                            </div>
                        ) : null}
                    </div>
                    <div className="tk-card">
                        <h6>SLA</h6>
                        {clocks.length ? clocks.map((c) => <div key={c.text} className={`tk-sla tk-sla-${c.tone} d-block mb-1`}>{c.text}</div>) : <div className="small text-muted">No targets.</div>}
                        <div className="small text-muted mt-1">Targets: first reply {t.sla?.responseMinutes} min, resolution {t.sla?.resolutionHours} h. Waiting on {isStaffTicket ? "the staff member" : "the customer"} doesn't count.</div>
                    </div>
                    {t.rating ? (
                        <div className="tk-card">
                            <h6>Rating</h6>
                            <div aria-label={`${t.rating} out of 5`}>{[1, 2, 3, 4, 5].map((n) => <BsStarFill key={n} className={n <= t.rating ? "text-warning" : "text-light"} aria-hidden="true" />)}</div>
                            {t.feedBack ? <p className="small mb-0 mt-1">{t.feedBack}</p> : null}
                        </div>
                    ) : null}
                    <div className="tk-card">
                        <h6>Activity</h6>
                        <Activity events={events} labels={EVENT_LABEL} />
                    </div>
                </aside>
            </div>

            {dialog === "resolve" ? (
                <Dialog title="Resolve the ticket" onClose={() => setDialog(null)} footer={<button type="button" className="btn btn-success" disabled={busy || text.trim().length < 2} onClick={() => run(() => ticketApi.resolve(t.ticketId, text.trim()), "Resolved")}>Resolve</button>}>
                    <label className="form-label small" htmlFor="tk-resolution">What was done (sent to {raiserLabel})</label>
                    <textarea id="tk-resolution" className="form-control" rows={4} maxLength={4000} value={text} onChange={(e) => setText(e.target.value)} />
                    <p className="small text-muted mt-2 mb-0">{isStaffTicket ? "The staff member" : "The customer"} can say it isn't solved; otherwise it closes on its own.</p>
                </Dialog>
            ) : null}
            {dialog === "assign" ? (
                <Dialog title={t.assignee?.id ? "Reassign" : "Assign"} onClose={() => setDialog(null)} footer={<button type="button" className="btn btn-primary" disabled={busy || !person} onClick={() => { const [type, id] = person.split(":"); run(() => ticketApi.reassign(t.ticketId, { type, id }, text.trim()), "Assigned"); }}>Assign</button>}>
                    <label className="form-label small" htmlFor="tk-person">To</label>
                    <select id="tk-person" className="form-select" value={person} onChange={(e) => setPerson(e.target.value)}>
                        <option value="">Choose...</option>
                        <optgroup label="Admins">{people.filter((p) => p.type === "admin").map((p) => <option key={`a${p.id}`} value={`admin:${p.id}`}>{p.name}</option>)}</optgroup>
                        {!isStaffTicket ? <optgroup label="Staff who handle tickets">{people.filter((p) => p.type === "staff").map((p) => <option key={`s${p.id}`} value={`staff:${p.id}`}>{p.name} · {p.hubId}</option>)}</optgroup> : null}
                    </select>
                    <label className="form-label small mt-3" htmlFor="tk-note">Note (optional)</label>
                    <input id="tk-note" className="form-control" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
                    {!isStaffTicket ? <p className="small text-muted mt-2 mb-0">The ticket keeps its hub ({t.hubId || "none"}) for reports.</p> : null}
                </Dialog>
            ) : null}
            {dialog === "appeal" ? (
                <Dialog title="Decide the appeal" onClose={() => setDialog(null)} footer={(
                    <>
                        {t.appeal?.by && t.appeal.by !== "system" ? <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => run(() => ticketApi.decideAppeal(t.ticketId, "return", "staff", text.trim()), "Returned")}>Return to {t.appeal.byName || "staff"}</button> : null}
                        <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => run(() => ticketApi.decideAppeal(t.ticketId, "return", "pool", text.trim()), "Returned to the pool")}>Return to the pool</button>
                        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => run(() => ticketApi.decideAppeal(t.ticketId, "take", null, text.trim()), "Taken")}>Take it</button>
                    </>
                )}>
                    <p className="small"><strong>{t.appeal?.byName || t.appeal?.by}</strong>: {t.appeal?.reason}</p>
                    <label className="form-label small" htmlFor="tk-appeal-note">Note to the staff member (optional)</label>
                    <textarea id="tk-appeal-note" className="form-control" rows={3} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
                </Dialog>
            ) : null}
            {dialog === "return" ? (
                <Dialog title="Return to the pool" onClose={() => setDialog(null)} footer={<button type="button" className="btn btn-primary" disabled={busy} onClick={() => run(() => ticketApi.returnToPool(t.ticketId, text.trim()), "Returned to the pool")}>Return</button>}>
                    <label className="form-label small" htmlFor="tk-return">Note (optional)</label>
                    <input id="tk-return" className="form-control" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
                </Dialog>
            ) : null}
            {dialog === "close" ? (
                <Dialog title="Close the ticket" onClose={() => setDialog(null)} footer={<button type="button" className="btn btn-danger" disabled={busy} onClick={() => run(() => ticketApi.close(t.ticketId, text.trim()), "Closed")}>Close</button>}>
                    <p className="small">Closes it now, without waiting for {raiserLabel}.</p>
                    <label className="form-label small" htmlFor="tk-close">Note (optional)</label>
                    <input id="tk-close" className="form-control" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
                </Dialog>
            ) : null}
            {dialog === "reopen" ? (
                <Dialog title="Reopen the ticket" onClose={() => setDialog(null)} footer={<button type="button" className="btn btn-primary" disabled={busy} onClick={() => run(() => ticketApi.reopen(t.ticketId, text.trim()), "Reopened")}>Reopen</button>}>
                    <p className="small">Possible until {dayText(can.reopenUntil)}. The resolution SLA starts again.</p>
                    <label className="form-label small" htmlFor="tk-reopen">Why (optional)</label>
                    <input id="tk-reopen" className="form-control" maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
                </Dialog>
            ) : null}
        </div>
    );
};

export default TicketDetail;
